/*
 * Thin HTTP client. Components never call fetch directly; they go
 * through the hooks in src/hooks/* which call this client.
 *
 * Conventions:
 *   - All non-2xx responses throw an `ApiError` with the parsed
 *     RFC 7807 problem body when available.
 *   - 409 with a `conflict_reason` is preserved as a `ConflictError`
 *     so the optimistic-version-mismatch flow (ADR-0011, ConflictToast)
 *     can pattern-match on `error.conflictReason`.
 */

import type {
  AddLineRequest,
  AddParticipantRequest,
  ConflictProblem,
  ConflictReason,
  CreateManualReceiptRequest,
  CreateReceiptRequest,
  CreateReceiptResponse,
  CreateTripRequest,
  CurrentSlugResponse,
  GoneProblem,
  LineItem,
  MarkDebtPaidRequest,
  Participant,
  ParticipantList,
  Problem,
  ReceiptDetail,
  ReceiptList,
  RotateTripResponse,
  Settlement,
  SettlementPlan,
  TagConsumerRequest,
  Trip,
  UpdateLineRequest,
  UpdateParticipantRequest,
  UpdateReceiptRequest,
  UpdateTripRequest,
  VersionedRequest,
} from "./types";

// API base URL. In dev we hit MSW which intercepts /v1; in production
// this will point at the API GW HTTP API URL — supplied via Vite env.
const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/v1";

export class ApiError extends Error {
  status: number;
  problem?: Problem;

  constructor(message: string, status: number, problem?: Problem) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }
}

export class ConflictError extends ApiError {
  conflictReason?: ConflictReason;
  currentVersion?: number;
  // ADR-0029: populated only on conflict_reason === "trip_not_ready".
  blockingCount?: number;
  blockingReceiptIds?: string[];

  constructor(message: string, problem: ConflictProblem) {
    super(message, 409, problem);
    this.name = "ConflictError";
    this.conflictReason = problem.conflict_reason;
    this.currentVersion = problem.current_version;
    this.blockingCount = problem.blocking_count;
    this.blockingReceiptIds = problem.blocking_receipt_ids;
  }
}

export class GoneError extends ApiError {
  restorableUntil?: string;

  constructor(message: string, problem: GoneProblem) {
    super(message, 410, problem);
    this.name = "GoneError";
    this.restorableUntil = problem.restorable_until;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers: {
      Accept: "application/json",
      ...(opts.body !== undefined
        ? { "Content-Type": "application/json" }
        : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  };

  const res = await fetch(`${API_BASE}${path}`, init);

  if (res.status === 204) {
    // No content. Cast through unknown for the rare void caller.
    return undefined as unknown as T;
  }

  const contentType = res.headers.get("content-type") ?? "";
  const isJson =
    contentType.includes("application/json") ||
    contentType.includes("application/problem+json");

  if (!res.ok) {
    const problem: Problem | undefined = isJson
      ? ((await res.json()) as Problem)
      : undefined;
    const message = problem?.title ?? `HTTP ${res.status}`;
    if (res.status === 409 && problem) {
      throw new ConflictError(message, problem as ConflictProblem);
    }
    if (res.status === 410 && problem) {
      throw new GoneError(message, problem as GoneProblem);
    }
    throw new ApiError(message, res.status, problem);
  }

  if (!isJson) {
    return undefined as unknown as T;
  }
  return (await res.json()) as T;
}

// ── Trips ──────────────────────────────────────────────────────────
export const api = {
  createTrip: (body: CreateTripRequest) =>
    request<Trip>("/trips", { method: "POST", body }),

  getTrip: (tripId: string) => request<Trip>(`/trips/${tripId}`),

  updateTrip: (tripId: string, body: UpdateTripRequest) =>
    request<Trip>(`/trips/${tripId}`, { method: "PATCH", body }),

  deleteTrip: (tripId: string) =>
    request<void>(`/trips/${tripId}`, { method: "DELETE" }),

  closeTrip: (tripId: string, body: VersionedRequest) =>
    request<Trip>(`/trips/${tripId}/close`, { method: "POST", body }),

  reopenTrip: (tripId: string, body: VersionedRequest) =>
    request<Trip>(`/trips/${tripId}/reopen`, { method: "POST", body }),

  restoreTrip: (tripId: string) =>
    request<Trip>(`/trips/${tripId}/restore`, { method: "POST" }),

  rotateTripSlug: (tripId: string) =>
    request<RotateTripResponse>(`/trips/${tripId}/rotate`, { method: "POST" }),

  getCurrentSlug: (tripId: string) =>
    request<CurrentSlugResponse>(`/trips/${tripId}/current-slug`),

  // ── Participants ────────────────────────────────────────────────
  listParticipants: (tripId: string) =>
    request<ParticipantList>(`/trips/${tripId}/participants`),

  addParticipant: (tripId: string, body: AddParticipantRequest) =>
    request<Participant>(`/trips/${tripId}/participants`, {
      method: "POST",
      body,
    }),

  updateParticipant: (
    tripId: string,
    pid: string,
    body: UpdateParticipantRequest,
  ) =>
    request<Participant>(`/trips/${tripId}/participants/${pid}`, {
      method: "PATCH",
      body,
    }),

  removeParticipant: (tripId: string, pid: string) =>
    request<void>(`/trips/${tripId}/participants/${pid}`, {
      method: "DELETE",
    }),

  // ── Receipts ────────────────────────────────────────────────────
  listReceipts: (tripId: string) =>
    request<ReceiptList>(`/trips/${tripId}/receipts`),

  createReceipt: (tripId: string, body: CreateReceiptRequest) =>
    request<CreateReceiptResponse>(`/trips/${tripId}/receipts`, {
      method: "POST",
      body,
    }),

  // ADR-0031 (US-20): manual, photo-less receipt with inline lines. Returns
  // the full ReceiptDetail (born confirmed, source=manual) — no upload object.
  createManualReceipt: (tripId: string, body: CreateManualReceiptRequest) =>
    request<ReceiptDetail>(`/trips/${tripId}/receipts/manual`, {
      method: "POST",
      body,
    }),

  getReceipt: (tripId: string, rid: string) =>
    request<ReceiptDetail>(`/trips/${tripId}/receipts/${rid}`),

  updateReceipt: (tripId: string, rid: string, body: UpdateReceiptRequest) =>
    request<ReceiptDetail>(`/trips/${tripId}/receipts/${rid}`, {
      method: "PATCH",
      body,
    }),

  deleteReceipt: (tripId: string, rid: string) =>
    request<void>(`/trips/${tripId}/receipts/${rid}`, { method: "DELETE" }),

  parseReceipt: (tripId: string, rid: string) =>
    request<ReceiptDetail>(`/trips/${tripId}/receipts/${rid}/parse`, {
      method: "POST",
    }),

  confirmReceipt: (tripId: string, rid: string, body: VersionedRequest) =>
    request<ReceiptDetail>(`/trips/${tripId}/receipts/${rid}/confirm`, {
      method: "POST",
      body,
    }),

  // ── Lines ───────────────────────────────────────────────────────
  addLine: (tripId: string, rid: string, body: AddLineRequest) =>
    request<LineItem>(`/trips/${tripId}/receipts/${rid}/lines`, {
      method: "POST",
      body,
    }),

  updateLine: (
    tripId: string,
    rid: string,
    lid: string,
    body: UpdateLineRequest,
  ) =>
    request<LineItem>(`/trips/${tripId}/receipts/${rid}/lines/${lid}`, {
      method: "PATCH",
      body,
    }),

  deleteLine: (tripId: string, rid: string, lid: string) =>
    request<void>(`/trips/${tripId}/receipts/${rid}/lines/${lid}`, {
      method: "DELETE",
    }),

  // ── Consumers ───────────────────────────────────────────────────
  tagConsumer: (
    tripId: string,
    rid: string,
    lid: string,
    pid: string,
    body?: TagConsumerRequest,
  ) =>
    request(
      `/trips/${tripId}/receipts/${rid}/lines/${lid}/consumers/${pid}`,
      { method: "PUT", body: body ?? {} },
    ),

  untagConsumer: (tripId: string, rid: string, lid: string, pid: string) =>
    request<void>(
      `/trips/${tripId}/receipts/${rid}/lines/${lid}/consumers/${pid}`,
      { method: "DELETE" },
    ),

  applySharedByAll: (
    tripId: string,
    rid: string,
    lid: string,
    body: VersionedRequest,
  ) =>
    request<LineItem>(
      `/trips/${tripId}/receipts/${rid}/lines/${lid}/shared-by-all`,
      { method: "POST", body },
    ),

  removeSharedByAll: (tripId: string, rid: string, lid: string) =>
    request<void>(
      `/trips/${tripId}/receipts/${rid}/lines/${lid}/shared-by-all`,
      { method: "DELETE" },
    ),

  // ── Opt-outs ────────────────────────────────────────────────────
  optOut: (tripId: string, rid: string, pid: string) =>
    request(`/trips/${tripId}/receipts/${rid}/opt-outs/${pid}`, {
      method: "PUT",
    }),

  cancelOptOut: (tripId: string, rid: string, pid: string) =>
    request<void>(`/trips/${tripId}/receipts/${rid}/opt-outs/${pid}`, {
      method: "DELETE",
    }),

  // ── Settlements (single surface, ADR-0025) ──────────────────────
  getSuggestedSettlements: (tripId: string) =>
    request<SettlementPlan>(`/trips/${tripId}/settlements/suggested`),

  markDebtPaid: (
    tripId: string,
    fromPid: string,
    toPid: string,
    body: MarkDebtPaidRequest,
  ) =>
    request<Settlement>(
      `/trips/${tripId}/settlements/${fromPid}/${toPid}`,
      { method: "PUT", body },
    ),

  unmarkDebtPaid: (tripId: string, fromPid: string, toPid: string) =>
    request<void>(`/trips/${tripId}/settlements/${fromPid}/${toPid}`, {
      method: "DELETE",
    }),
};
